import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Send, Clock, Save } from "lucide-react";
import dayjs from "dayjs";

const SaveOptionsDialog = ({ open, onClose, onSave, loading, setLoading }) => {
  const [selectedOption, setSelectedOption] = useState("save");
  const [scheduledDateTime, setScheduledDateTime] = useState(
    dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
  );
  const [error, setError] = useState("");

  const handleSave = () => {
    setError("");

    // Validate schedule time
    if (selectedOption === "schedule") {
      if (!scheduledDateTime || dayjs(scheduledDateTime).isBefore(dayjs())) {
        setError("Scheduled time must be in the future");
        return;
      }
    }

    // Pass scheduledTime only for schedule option
    const scheduledTime =
      selectedOption === "schedule"
        ? dayjs(scheduledDateTime).format("YYYY-MM-DDTHH:mm:ss")
        : null;

    onSave(selectedOption, scheduledTime);
  };

  const handleClose = () => {
    setError("");
    setScheduledDateTime(dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
    setSelectedOption("save");
    setLoading(false);
    onClose();
  };

  const handleDateTimeChange = (e) => {
    setScheduledDateTime(e.target.value);
    setError("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          pb: 2,
        }}
      >
        <Typography variant="h5" fontWeight="600">
          Save Email Template
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
          Choose how you want to proceed with your email template
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
          >
            {/* Send Now Option */}
            <Box
              sx={{
                border: "2px solid",
                borderColor: selectedOption === "now" ? "#667eea" : "#e2e8f0",
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#667eea",
                  backgroundColor: "#f8f9ff",
                },
              }}
              onClick={() => setSelectedOption("now")}
            >
              <FormControlLabel
                value="now"
                control={<Radio />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Send size={20} />
                    <Box>
                      <Typography fontWeight="600">Send Now</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Save template and add to email bucket for immediate
                        sending
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>

            {/* Schedule Option */}
            <Box
              sx={{
                border: "2px solid",
                borderColor:
                  selectedOption === "schedule" ? "#667eea" : "#e2e8f0",
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#667eea",
                  backgroundColor: "#f8f9ff",
                },
              }}
              onClick={() => setSelectedOption("schedule")}
            >
              <FormControlLabel
                value="schedule"
                control={<Radio />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Clock size={20} />
                    <Box>
                      <Typography fontWeight="600">Schedule Email</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Save template and schedule for later delivery
                      </Typography>
                    </Box>
                  </Box>
                }
              />

              {selectedOption === "schedule" && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <TextField
                    label="Schedule Date & Time"
                    type="datetime-local"
                    value={scheduledDateTime}
                    onChange={handleDateTimeChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: dayjs().format('YYYY-MM-DDTHH:mm'),
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Save Only Option */}
            <Box
              sx={{
                border: "2px solid",
                borderColor: selectedOption === "save" ? "#667eea" : "#e2e8f0",
                borderRadius: 2,
                p: 2.5,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#667eea",
                  backgroundColor: "#f8f9ff",
                },
              }}
              onClick={() => setSelectedOption("save")}
            >
              <FormControlLabel
                value="save"
                control={<Radio />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Save size={20} />
                    <Box>
                      <Typography fontWeight="600">
                        Save Template Only
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Save template without adding to email bucket
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>
          </RadioGroup>
        </FormControl>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          // disabled={loading}
          sx={{
            textTransform: "none",
            color: "#64748b",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          sx={{
            textTransform: "none",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            px: 3,
            "&:hover": {
              background: "linear-gradient(135deg, #5568d3 0%, #6941a0 100%)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : selectedOption === "now" ? (
            "Send Now"
          ) : selectedOption === "schedule" ? (
            "Schedule Email"
          ) : (
            "Save Template"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveOptionsDialog;